/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormSection } from '@/shared/ui/components/form';
import { SearchAutocomplete, type SearchAutocompleteOption } from '@/shared/ui/components/form/SearchAutocomplete';
import { MaskedNumberField } from '@/features/property-management/components/PropertyDrawer/MaskedNumberField';
import { Text } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import type { SaleFormData } from './schema';

interface CreateSaleFieldsProps {
  form: UseFormReturn<SaleFormData>;
  propertyOptions: SearchAutocompleteOption[];
  clientOptions: SearchAutocompleteOption[];
  brokerOptions: SearchAutocompleteOption[];
  hasValidationErrors: boolean;
}

/**
 * Form body for the create-sale drawer: what the deal-won path would have
 * inherited (property, buyer, value, commission, broker).
 *
 * The owner is deliberately not a field here: the property already names
 * its registered owner, and the backend reads it from there — the same
 * fact the deal-won path reads, asked for only once.
 */
export function CreateSaleFields({
  form,
  propertyOptions,
  clientOptions,
  brokerOptions,
  hasValidationErrors,
}: CreateSaleFieldsProps) {
  const { t } = useTranslation('features/sales');
  const { errors } = form.formState;

  return (
    <div className="flex flex-col gap-(--space-3)">
      <Text component="span" size="xs" className="text-(--text-tertiary)">
        {t('create.noDealNote')}
      </Text>

      <FormSection title={t('create.detailsSectionTitle')} variant="quiet">
        <div className="flex flex-col gap-(--space-4)">
          <Field label={t('create.property')} error={errors.propertyId?.message}>
            <Controller
              name="propertyId"
              control={form.control}
              render={({ field }) => (
                <SearchAutocomplete
                  placeholder={t('create.propertyPlaceholder')}
                  options={propertyOptions}
                  value={field.value || null}
                  onChange={(val) => field.onChange(val ?? '')}
                />
              )}
            />
          </Field>

          <Field label={t('create.buyer')} error={errors.buyerId?.message}>
            <Controller
              name="buyerId"
              control={form.control}
              render={({ field }) => (
                <SearchAutocomplete
                  placeholder={t('create.buyerPlaceholder')}
                  options={clientOptions}
                  value={field.value || null}
                  onChange={(val) => field.onChange(val ?? '')}
                />
              )}
            />
          </Field>

          <div className="flex gap-(--space-3)">
            <div className="flex-1">
              <Controller
                name="acceptedValue"
                control={form.control}
                render={({ field }) => (
                  <MaskedNumberField
                    label={t('create.acceptedValue')}
                    error={errors.acceptedValue?.message}
                    suffix="R$"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>

            <div className="flex-1">
              <Controller
                name="commissionPercentage"
                control={form.control}
                render={({ field }) => (
                  <MaskedNumberField
                    label={t('create.commissionPercentage')}
                    suffix="%"
                    maxDigits={4}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          </div>

          <Field label={t('create.responsibleBroker')}>
            <Controller
              name="responsibleBrokerEmail"
              control={form.control}
              render={({ field }) => (
                <SearchAutocomplete
                  placeholder={t('create.responsibleBrokerPlaceholder')}
                  options={brokerOptions}
                  value={field.value || null}
                  onChange={(val) => field.onChange(val ?? '')}
                />
              )}
            />
          </Field>
        </div>
      </FormSection>

      {hasValidationErrors && (
        <div
          role="alert"
          className={cn(
            'rounded-(--radius-md) border px-(--space-3) py-(--space-3)',
            'border-[color-mix(in_srgb,var(--status-error)_20%,transparent)]',
            'bg-[color-mix(in_srgb,var(--status-error)_8%,transparent)]',
            'text-[length:var(--text-small)] text-[var(--status-error)]',
          )}
        >
          {t('create.errors.fillRequired')}
        </div>
      )}
    </div>
  );
}

/**
 * Labeled wrapper around a Controller-driven field that isn't a TextInput.
 *
 * @param props - The field's label, error and control.
 * @returns The labelled field row.
 */
function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-(--space-1)">
      <Text component="label" size="xs" fw={500} className="text-(--text-secondary)">
        {label}
      </Text>
      {children}
      {error && (
        <Text component="span" size="xs" className="text-(--status-error)">
          {error}
        </Text>
      )}
    </div>
  );
}
